import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const FOUNDER_PHOTO = '/assets/founder.jpg';
const WHATSAPP_NUM_DISPLAY = '+91 80876 45456';
const WHATSAPP_PHONE = '918087645456';

const CONSULT_TYPES = [
  { id: 'video',    label: 'Video Consult',    sub: 'From the comfort of your home',  icon: 'fa-video' },
  { id: 'in_person',label: 'In-Person',        sub: 'ANAMIVA Clinic, Goa',            icon: 'fa-hospital-user' },
  { id: 'voice',    label: 'Voice Consult',    sub: 'Discreet voice-only call',       icon: 'fa-phone-volume' },
];

const buildText = (typeLabel) =>
  `Hello Dr. Vikram, I would like to book an INTIVIV consultation.%0A%0AType: ${encodeURIComponent(typeLabel)}%0APlease share available slots — thank you.`;

export default function Consultation() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState('video');
  const ct = CONSULT_TYPES.find(c => c.id === selected) || CONSULT_TYPES[0];

  const waText = buildText(ct.label);
  const waUrl = `https://wa.me/${WHATSAPP_PHONE}?text=${waText}`;

  const copyNumber = async () => {
    try { await navigator.clipboard.writeText('+' + WHATSAPP_PHONE); } catch { /* */ }
  };

  return (
    <div style={{ minHeight: '100vh', paddingBottom: 'var(--bottom-h)' }}>
      {/* HERO — single focused founder card */}
      <div
        className="immersive-card"
        data-testid="consult-hero"
        style={{ margin: '16px', padding: '18px', display: 'flex', alignItems: 'center', gap: '16px', border: '1px solid var(--border-soft)', background: 'var(--bg2)' }}
      >
        <div style={{ width: 84, height: 84, minWidth: 84, borderRadius: '50%', border: '2px solid var(--gold)', overflow: 'hidden', flexShrink: 0, boxShadow: '0 0 28px rgba(212,175,55,0.28)' }}>
          <img src={FOUNDER_PHOTO} alt="Dr. Vikram Dukle" data-testid="consult-founder-photo" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="eyebrow" style={{ marginBottom: 2 }}>Your Specialist</div>
          <div
            data-testid="consult-founder-name"
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: '1.8rem',
              lineHeight: 1.1,
              marginBottom: 6,
              fontWeight: 800,
              background: 'linear-gradient(135deg, #FFE9A6 0%, #D4AF37 50%, #8C6E1C 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              textShadow: '0 0 24px rgba(212,175,55,0.45)',
              letterSpacing: '0.01em',
            }}
          >
            Dr. Vikram Dukle
          </div>
          <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
            Cosmetic Gynaecologist · Sexual Wellness Expert<br />
            ANAMIVA Clinic, Goa · 15+ years
          </div>
          <button onClick={() => navigate('/founder')} className="btn-ghost" style={{ fontSize: '0.71rem', padding: 0, marginTop: 6, color: 'var(--gold)', letterSpacing: '0.16em' }} data-testid="consult-meet-founder">
            MEET THE FOUNDER →
          </button>
        </div>
      </div>

      {/* STEP 1 */}
      <div style={{ padding: '20px 16px 8px' }}>
        <div className="eyebrow">Step 1 · Choose your consultation</div>
      </div>
      <div style={{ padding: '0 16px 16px', display: 'grid', gap: 10 }}>
        {CONSULT_TYPES.map(c => {
          const active = selected === c.id;
          return (
            <button key={c.id} type="button" onClick={() => setSelected(c.id)}
              data-testid={`consult-type-${c.id}`}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 14,
                padding: '14px 16px',
                background: active ? 'linear-gradient(135deg, rgba(212,175,55,0.10), rgba(10,10,10,0)), var(--bg2)' : 'var(--bg2)',
                border: active ? '1px solid var(--gold)' : '1px solid var(--border-soft)',
                cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--font-ui)', color: 'var(--text)',
                transition: 'all 0.2s',
                boxShadow: active ? '0 12px 32px -16px rgba(212,175,55,0.4)' : 'none',
              }}>
              <div style={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', background: active ? 'rgba(212,175,55,0.18)' : 'var(--gold-dim)', border: '1px solid ' + (active ? 'var(--gold)' : 'var(--border)'), flexShrink: 0 }}>
                <i className={`fa-solid ${c.icon}`} style={{ color: 'var(--gold)', fontSize: '1.16rem' }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '1.01rem', fontWeight: 600, marginBottom: 2 }}>{c.label}</div>
                <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)' }}>{c.sub}</div>
              </div>
              <div style={{ width: 18, height: 18, borderRadius: '50%', border: '1.5px solid ' + (active ? 'var(--gold)' : 'var(--border)'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {active && <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--gold)', boxShadow: '0 0 8px var(--gold)' }} />}
              </div>
            </button>
          );
        })}
      </div>

      {/* STEP 2 — anchor link to WhatsApp (works inside iframe + native apps) */}
      <div style={{ padding: '4px 16px 8px' }}>
        <div className="eyebrow">Step 2 · Confirm on WhatsApp</div>
      </div>
      <div style={{ padding: '0 16px 6px' }}>
        <a
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="consult-whatsapp-main"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
            width: '100%', padding: '16px', textDecoration: 'none',
            background: 'linear-gradient(135deg, #25D366, #128C7E)',
            color: 'white',
            fontSize: '0.87rem', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase',
            fontFamily: 'var(--font-ui)',
            boxShadow: '0 12px 32px -12px rgba(37,211,102,0.6)',
          }}
        >
          <i className="fa-brands fa-whatsapp" style={{ fontSize: '1.5rem' }} />
          BOOK ON WHATSAPP
        </a>
      </div>

      {/* Phone number — copy only (call CTA removed per design) */}
      <div style={{ padding: '0 16px 12px', display: 'flex', gap: 10 }}>
        <button
          type="button"
          onClick={copyNumber}
          data-testid="consult-copy"
          style={{
            flex: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            padding: '11px',
            background: 'var(--bg2)', border: '1px solid var(--border-soft)',
            color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.81rem',
            letterSpacing: '0.1em', fontFamily: 'var(--font-ui)',
          }}
        >
          <i className="fa-regular fa-copy" style={{ color: 'var(--gold)' }} /> {WHATSAPP_NUM_DISPLAY}
        </button>
      </div>
      <div style={{ paddingBottom: 18 }} />

      {/* Shimmering trust */}
      <div style={{ padding: '4px 16px 30px' }}>
        <div className="shimmer-bar" data-testid="consult-trust-line">
          <i className="fa-solid fa-shield-halved" style={{ marginRight: 8 }} />
          All conversations are confidential.
        </div>
      </div>
    </div>
  );
}
